import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, Check, CheckCheck, ChevronLeft, ChevronRight, Crown, Tag, Flame, Gem, Package, Settings as SettingsIcon } from 'lucide-react';
import { channelApi } from '../services/api';

const BADGE_STYLES = {
  'High Value': 'bg-green-100 text-green-800 border-green-200',
  'Hot Deal': 'bg-red-100 text-red-700 border-red-200',
  'Priority': 'bg-amber-100 text-amber-800 border-amber-200',
  'Limited': 'bg-purple-100 text-purple-700 border-purple-200',
  'Verified': 'bg-blue-100 text-blue-700 border-blue-200',
  'New Post': 'bg-slate-100 text-slate-600 border-slate-200',
};

const CATEGORY_LABELS = {
  gold_trade: 'Gold Trade',
  oil_energy: 'Oil & Energy',
  commodities: 'Commodities',
  machinery: 'Machinery',
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => { fetchNotifications(); }, [page]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data } = await channelApi.getNotifications({ page, limit: 15 });
      setNotifications(data.notifications || []);
      setTotalPages(data.pages || 1);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await channelApi.markRead(notifId);
      setNotifications(prev => prev.map(n => n.notif_id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await channelApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { /* ignore */ }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0A192F] rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0A192F]">Notifications</h1>
              <p className="text-xs text-slate-500">{unreadCount} unread</p>
            </div>
          </div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="notif-page-mark-all">
                <CheckCheck className="w-4 h-4 mr-1" /> Mark All Read
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/notifications/settings')} data-testid="notif-settings-btn">
              <SettingsIcon className="w-4 h-4 mr-1" /> Preferences
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
                <div className="h-3 bg-slate-200 rounded w-full" />
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-white border border-slate-200 p-16 text-center">
            <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No notifications yet</p>
            <p className="text-sm text-slate-400 mt-1">You'll be notified when high-value deals are posted</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(n => (
              <button
                key={n.notif_id}
                onClick={() => { handleMarkRead(n.notif_id); navigate(`/channel/${n.post_id}`); }}
                className={`w-full text-left bg-white border transition-all hover:shadow-sm group ${!n.is_read ? 'border-[#EA580C]/30 bg-orange-50/30' : 'border-slate-200'}`}
                data-testid={`notif-item-${n.notif_id}`}
              >
                <div className="p-4 flex items-start gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${!n.is_read ? 'bg-[#EA580C]' : 'bg-slate-200'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {n.badge && (
                        <Badge className={`${BADGE_STYLES[n.badge] || BADGE_STYLES['New Post']} text-[10px] font-bold border`}>
                          {n.is_priority && <Crown className="w-3 h-3 mr-0.5" />}
                          {n.badge}
                        </Badge>
                      )}
                      {n.category && (
                        <span className="text-[10px] text-slate-400">{CATEGORY_LABELS[n.category] || n.category}</span>
                      )}
                      {n.deal_value && (
                        <span className="text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">{n.deal_value}</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-[#0A192F] group-hover:text-[#EA580C] transition-colors">{n.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.teaser}</p>
                    <p className="text-[10px] text-slate-400 mt-2">{formatDate(n.created_at)}</p>
                  </div>
                  {!n.is_read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleMarkRead(n.notif_id); }}
                      className="text-slate-400 hover:text-[#0A192F] shrink-0 mt-1"
                      data-testid={`mark-read-${n.notif_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-6">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default NotificationsPage;
